import { Injectable } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject } from 'rxjs';
import { CommercialImpactInput } from '../../viewmodels/commercial-impacts/commercial-impact-input';
import { CommercialImpactItem } from '../../viewmodels/commercial-impacts/commercial-impact-item';
import { CommercialImpacts } from '../../viewmodels/commercial-impacts/commercial-impacts';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';

export enum CommercialImpactItemType {
  ProductionStream = 1,
  Labor,
  Material,
  Other,
}

@Injectable({
  providedIn: 'root',
})
export class CommercialImpactsService {
  commercialImpacts = new BehaviorSubject<CommercialImpacts>(null);

  constructor(
    private bsModalRef: BsModalRef,
    private modalService: BsModalService,
    private i18NextPipe: I18NextPipe
  ) { }

  setItem(item: CommercialImpacts) {
    this.commercialImpacts.next(item);
  }

  getItem(): CommercialImpacts {
    if (!this.commercialImpacts.value) {
      return;
    }

    return this.commercialImpacts.value;
  }

  calculateCurrentTotal(commercialImpacts: CommercialImpacts) {
    if (!commercialImpacts) {
      return;
    }

    let result: number = 0;

    result += this.calcTotal(commercialImpacts.productionStream.inputs, CommercialImpactItemType.ProductionStream);
    result += this.calcTotal(commercialImpacts.labor.inputs, CommercialImpactItemType.Labor);
    result += this.calcTotal(commercialImpacts.material.inputs, CommercialImpactItemType.Material);
    result += this.calcTotal(commercialImpacts.other.inputs, CommercialImpactItemType.Other);

    return +result.toFixed(2);
  }

  calculateCurrentItemTotal(itemType: CommercialImpactItemType) {
    switch (itemType) {
      case CommercialImpactItemType.ProductionStream:
        return this.calcTotal(this.commercialImpacts.value.productionStream.inputs, itemType);
      case CommercialImpactItemType.Labor:
        return this.calcTotal(this.commercialImpacts.value.labor.inputs, itemType);
      case CommercialImpactItemType.Material:
        return this.calcTotal(this.commercialImpacts.value.material.inputs, itemType);
      case CommercialImpactItemType.Other:
        return this.calcTotal(this.commercialImpacts.value.other.inputs, itemType);
    }
  }

  private calcTotal(inputs: CommercialImpactInput[], itemType: CommercialImpactItemType) {
    let total = 0;

    for (const input of inputs) {
      if (itemType === CommercialImpactItemType.ProductionStream) {
        input.totalCost = ((input.quantity * input.unitsPerHour * input.valuePerUnit) / 100) * input.impact;
      } else {
        input.totalCost = input.quantity * input.valuePerHour;
      }
      total += input.totalCost;
    }

    return +total.toFixed(2);
  }

  getCommercialImpactItem(itemType: CommercialImpactItemType): CommercialImpactItem {
    switch (itemType) {
      case CommercialImpactItemType.ProductionStream:
        return this.commercialImpacts.value.productionStream;
      case CommercialImpactItemType.Labor:
        return this.commercialImpacts.value.labor;
      case CommercialImpactItemType.Material:
        return this.commercialImpacts.value.material;
      case CommercialImpactItemType.Other:
        return this.commercialImpacts.value.other;
    }
  }

  getCommercialImpactInputs(itemType: CommercialImpactItemType): CommercialImpactInput[] {
    switch (itemType) {
      case CommercialImpactItemType.ProductionStream:
        return this.commercialImpacts.value.productionStream.inputs;
      case CommercialImpactItemType.Labor:
        return this.commercialImpacts.value.labor.inputs;
      case CommercialImpactItemType.Material:
        return this.commercialImpacts.value.material.inputs;
      case CommercialImpactItemType.Other:
        return this.commercialImpacts.value.other.inputs;
    }
  }

  calculateCurrentInputTotal(itemType: CommercialImpactItemType, index: number) {
    let item: CommercialImpactInput;
    let total: number;

    switch (itemType) {
      case CommercialImpactItemType.ProductionStream:
        item = this.commercialImpacts.value.productionStream.inputs[index];
        total = ((item.quantity * item.unitsPerHour * item.valuePerUnit) / 100) * item.impact;
        break;
      case CommercialImpactItemType.Labor:
        item = this.commercialImpacts.value.labor.inputs[index];
        total = item.quantity * item.valuePerHour;
        break;
      case CommercialImpactItemType.Material:
        item = this.commercialImpacts.value.material.inputs[index];
        total = item.quantity * item.valuePerHour;
        break;
      case CommercialImpactItemType.Other:
        item = this.commercialImpacts.value.other.inputs[index];
        total = item.quantity * item.valuePerHour;
        break;
    }

    return +total.toFixed(2);
  }

  addCommercialImpactInput(itemType: CommercialImpactItemType) {
    const newInput: CommercialImpactInput = new CommercialImpactInput();
    newInput.description = this.i18NextPipe.transform('CommercialImpactsService.NewInput');
    newInput.quantity = 0;
    newInput.impact = 0;
    newInput.unitsPerHour = 0;
    newInput.valuePerUnit = 0;
    newInput.valuePerHour = 0;
    newInput.totalCost = 0;

    switch (itemType) {
      case CommercialImpactItemType.ProductionStream:
        if (this.commercialImpacts.value.productionStream.inputs === null) {
          this.commercialImpacts.value.productionStream.inputs = [];
        }
        this.commercialImpacts.value.productionStream.inputs.push(newInput);
        break;
      case CommercialImpactItemType.Labor:
        if (this.commercialImpacts.value.labor.inputs === null) {
          this.commercialImpacts.value.labor.inputs = [];
        }
        this.commercialImpacts.value.labor.inputs.push(newInput);
        break;
      case CommercialImpactItemType.Material:
        if (this.commercialImpacts.value.material.inputs === null) {
          this.commercialImpacts.value.material.inputs = [];
        }
        this.commercialImpacts.value.material.inputs.push(newInput);
        break;
      case CommercialImpactItemType.Other:
        if (this.commercialImpacts.value.other.inputs === null) {
          this.commercialImpacts.value.other.inputs = [];
        }
        this.commercialImpacts.value.other.inputs.push(newInput);
        break;
    }
  }

  deleteCommercialImpactInput(itemType: CommercialImpactItemType, index: number) {
    this.bsModalRef = this.modalService.show(ConfirmationDialogComponent);

    let itemToDelete: CommercialImpactInput;
    switch (itemType) {
      case CommercialImpactItemType.ProductionStream:
        itemToDelete = this.commercialImpacts.value.productionStream.inputs[index];
        break;
      case CommercialImpactItemType.Labor:
        itemToDelete = this.commercialImpacts.value.labor.inputs[index];
        break;
      case CommercialImpactItemType.Material:
        itemToDelete = this.commercialImpacts.value.material.inputs[index];
        break;
      case CommercialImpactItemType.Other:
        itemToDelete = this.commercialImpacts.value.other.inputs[index];
        break;
    }

    this.bsModalRef.content.title = this.i18NextPipe.transform('CommercialImpactsService.DeleteInspectionInput');
    this.bsModalRef.content.messageTop = this.i18NextPipe.transform('CommercialImpactsService.InputWillBeDeletedContinue', { value: itemToDelete.description });

    this.bsModalRef.content.afterClosed.subscribe(confirm => {
      if (confirm) {
        switch (itemType) {
          case CommercialImpactItemType.ProductionStream:
            this.commercialImpacts.value.productionStream.inputs.splice(index, 1);
            break;
          case CommercialImpactItemType.Labor:
            this.commercialImpacts.value.labor.inputs.splice(index, 1);
            break;
          case CommercialImpactItemType.Material:
            this.commercialImpacts.value.material.inputs.splice(index, 1);
            break;
          case CommercialImpactItemType.Other:
            this.commercialImpacts.value.other.inputs.splice(index, 1);
            break;
        }
      }
    });
  }
}
